import React, { useEffect, useRef, useState } from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Table,
  UncontrolledTooltip,
} from "reactstrap"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import Filters from "components/Common/Filters"
import { Link, useLocation, useNavigate } from "react-router-dom"
import DeleteModal from "components/Common/DeleteModal"
import Paginations from "components/Common/Paginations"
import { useSelector } from "react-redux"
import isPermissionEnabled from "common/isPermissionEnabled"
import { useDispatch } from "react-redux"
import { getAllOrders, updateOrderStatus } from "store/actions/orderAction"
import TableShimmer from "components/Shared/TableShimmer"
import { getAllBranchs } from "store/actions/usersAction"
import { exportData } from "store/actions/commonAction"
import { pdfDownload } from "helpers/api_helper"
import { EXPORT_DATA } from "helpers/url_helper"
import DownloadXLSX from "components/Shared/DownloadXLSX"
import DownloadCSV from "components/Shared/DownloadCSV"
import {
  getPerPageOptions,
  orderStatus,
  statusListStatus,
  todayDate,
} from "components/Common/constants"
import Select from "react-select"
import { showAlert } from "store/reducers/alertReducer"
import { useTranslation } from "react-i18next"
import { setItemsPerPage } from "store/reducers/orderReducer"
import { getAppVersions } from "store/actions/customersAction"

const WEB_APP_VERSION = "1.0.0"

const expandPlatformFilter = platform =>
  platform?.flatMap(item => {
    const value = String(item.value || "")
    const key = value.toLowerCase()
    if (key === "android") return ["android", "Android"]
    if (key === "ios") return ["ios", "iOS"]
    if (key === "web") return ["web", "website"]
    return value
  }) || []

const getPlatformKeys = platform =>
  platform?.map(item => String(item.value || "").toLowerCase()) || []

const getAppVersionFilterOptions = (platform, apiVersions = []) => {
  const keys = getPlatformKeys(platform)
  const hasWeb = keys.includes("web")
  const hasMobile = keys.some(key => key === "android" || key === "ios")
  let options = []

  if (hasMobile) {
    options = (apiVersions || []).filter(Boolean)
  }
  if (hasWeb && !options.includes(WEB_APP_VERSION)) {
    options = [...options, WEB_APP_VERSION]
  }

  return options.map(item => ({
    label: item,
    value: item,
  }))
}

const Orders = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Orders | Sign Burger")

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const branchIdParam = searchParams.get("branchId")
  const orderTypeParam = searchParams.get("orderType")
  const platformParam = searchParams.get("platform")

  const minDate = searchParams.get("minDate") || todayDate
  const maxDate = searchParams.get("maxDate") || todayDate

  const {
    loading,
    isLoading,
    isUpdating,
    itemsPerPage,
    totalCount,
    totalPages,
    currentPage,
    orders,
  } = useSelector(state => state.Orders)
  const { branchsList } = useSelector(state => state.Users)

  const { appVersions } = useSelector(state => state.Customers)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [searchTerm2, setSearchTerm2] = useState("")

  const [selectedFilters, setSelectedFilters] = useState({
    sortOrder: parseInt(searchParams.get("sortOrder") || "2"),
    branchId: branchIdParam
      ? branchIdParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    status: parseInt(searchParams.get("status") || "0"),
    orderType: orderTypeParam
      ? orderTypeParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    platform: platformParam
      ? platformParam?.split(",").map(item => {
          const [label, value] = item.split(":")
          return { label, value }
        })
      : [],
    appVersion: "",
  })

  const flatpickrRef = useRef(null)

  const [dateRange, setDateRange] = useState({ min: minDate, max: maxDate })

  const filters = [
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
    {
      name: "branchId",
      type: "multiSelect",
      options: [
        // { label: "All", value: "" },
        ...branchsList.map(branch => ({
          label: language === "sa" ? branch.nameAr : branch.name,
          value: branch._id,
        })),
      ],
      placeholder: t("Select Branch"),
    },
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 0 },
        { label: t("Sent To Branch"), value: 1 },
        { label: t("Start Preparing"), value: 2 },
        { label: t("Prepared"), value: 3 },
        { label: t("Collected"), value: 4 },
        { label: t("Cancelled"), value: 5 },
      ],
      placeholder: t("Select Branch"),
    },
    {
      name: "orderType",
      type: "multiSelect",
      options: [
        { label: t("Pickup"), value: "pickup" },
        { label: t("Curbside"), value: "curbside" },
      ],
      placeholder: t("Select Order Type"),
    },
    {
      name: "platform",
      type: "multiSelect",
      options: [
        { label: t("Android"), value: "android" },
        { label: t("iOS"), value: "iOS" },
        { label: t("Web"), value: "web" },
      ],
      placeholder: t("Platform"),
    },
  ]

  if (selectedFilters?.platform?.length > 0) {
    filters.push({
      name: "appVersion",
      type: "select",
      options: getAppVersionFilterOptions(
        selectedFilters?.platform,
        appVersions
      ),
      placeholder: t("App Version"),
    })
  }

  useEffect(() => {
    fetchAllOrders()
    fetchAllBranchs()
  }, [])

  const fetchAllOrders = () => {
    const { status, sortOrder, branchId, orderType, platform, appVersion } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPlatform = expandPlatformFilter(platform)

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      orderBy: sortOrder,
      keyword: searchTerm,
      orderNo: searchTerm2 || "",
      appVersion: platform?.length > 0 ? appVersion : "",
      status: status,
      branchId: branchIds,
      orderType: updatedOrderType,
      platform: updatedPlatform,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }
    dispatch(getAllOrders(payload))
    setIsApiCalled(true)
  }

  useEffect(() => {
    const { platform } = selectedFilters
    const mobilePlatforms = getPlatformKeys(platform).filter(
      key => key === "android" || key === "ios"
    )
    if (mobilePlatforms.length > 0) {
      fetchAppVersions()
    }
  }, [selectedFilters])

  const fetchAppVersions = () => {
    const { platform } = selectedFilters
    const mobilePlatforms =
      platform?.filter(item => {
        const key = String(item.value || "").toLowerCase()
        return key === "android" || key === "ios"
      }) || []
    if (mobilePlatforms.length === 0) return
    dispatch(
      getAppVersions({ platform: expandPlatformFilter(mobilePlatforms) })
    )
  }

  const fetchAllBranchs = () => {
    dispatch(getAllBranchs())
  }

  const { userRole, accountType } = useSelector(state => state.Login)
  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    edit: null,
  })

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const isPermissionList = isPermissionEnabled(
      userRole,
      9,
      "order_1",
      accountType
    )
    const isPermissionEdit = isPermissionEnabled(
      userRole,
      9,
      "order_2",
      accountType
    )
    if (!isPermissionList) {
      navigate("/dashboard")
    }
    setUsersPermissions({
      list: isPermissionList,
      edit: isPermissionEdit,
    })
  }, [userRole])

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }

  const handleSearchChange2 = e => {
    setSearchTerm2(e.target.value)
  }

  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => {
      const updatedFilters = {
        ...prevFilters,
        [filterName]: selectedValue,
      }

      if (filterName === "platform" && !selectedValue?.length) {
        updatedFilters.appVersion = ""
      }

      return updatedFilters
    })
  }

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  const handleFetchOrders = (page = 1) => {
    const { status, sortOrder, branchId, orderType, platform, appVersion } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value) || []
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPlatform = expandPlatformFilter(platform)

    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    if (branchId?.length > 0) {
      const branchIdString = branchId
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("branchId", branchIdString)
    }
    if (orderType?.length > 0) {
      const orderTypeString = orderType
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("orderType", orderTypeString)
    }
    if (platform?.length > 0) {
      const platformString = platform
        .map(item => `${item.label}:${item.value}`)
        .join(",")
      searchParams.set("platform", platformString)
    }

    if (dateRange.min && dateRange.max) {
      searchParams.set("minDate", dateRange.min?.toString())
      searchParams.set("maxDate", dateRange.max?.toString())
    }

    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
      orderNo: searchTerm2 || "",
      appVersion: platform?.length > 0 ? appVersion : "",
      branchId: branchIds,
      status: Number(status),
      orderType: updatedOrderType,
      platform: updatedPlatform,
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
    }

    dispatch(getAllOrders(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`/orders?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchOrders(1)
  }

  const onPageChange = page => {
    handleFetchOrders(page)
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters, itemsPerPage])

  // useEffect(() => {
  //   if (dateRange.min && dateRange.max) {
  //     handleSearch()
  //   }
  // }, [dateRange])

  const handleDelete = () => {
    setShowDeleteModal(false)
  }

  const handleReset = () => {
    dispatch(setItemsPerPage(50))
    const payload = {
      perPage: 10,
      pageNo: 1,
      orderBy: 2,
      keyword: "",
      status: 0,
      dateRange: { min: todayDate, max: todayDate },
    }
    dispatch(getAllOrders(payload))
    setSelectedFilters({
      sortOrder: 2,
      branchId: [],
      status: 0,
      orderType: [],
      platform: [],
      appVersion: "",
    })
    setSearchTerm("")
    setSearchTerm2("")
    setDateRange({ min: todayDate, max: todayDate })
    if (flatpickrRef.current) {
      flatpickrRef.current.flatpickr.setDate([todayDate, todayDate])
    }
    navigate(`/orders?page=1`)
  }

  const formatPromiseDateTime = dateTimeString => {
    if (!dateTimeString) return "-"
    const date = new Date(dateTimeString)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    let hours = date.getHours()
    const meridiem = hours >= 12 ? "PM" : "AM"
    hours = hours % 12 || 12 // Convert 0 to 12 for 12-hour clock
    const minutes = String(date.getMinutes()).padStart(2, "0")
    const seconds = String(date.getSeconds()).padStart(2, "0")

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds} ${meridiem}`
  }

  const handleRowClick = id => {
    navigate(`/orders/order-details?id=${id}`)
  }

  const handleExcelDownload = async () => {
    const { status, branchId, orderType, platform, appVersion } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPlatform = expandPlatformFilter(platform)
    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      orderNo: searchTerm2 || "",
      type: "ORDER",
      exportType: "xlsx",
      branchId: branchIds || [],
      userId: "",
      orderType: updatedOrderType,
      platform: updatedPlatform,
      appVersion: platform?.length > 0 ? appVersion : "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const xlsxData = response
      DownloadXLSX(xlsxData, t("Order"))
    }
  }

  const exportToCSV = async () => {
    const { status, branchId, orderType, platform, appVersion } =
      selectedFilters
    const branchIds = branchId?.map(item => item.value)
    const updatedOrderType = orderType?.map(item => item.value) || []
    const updatedPlatform = expandPlatformFilter(platform)

    const payload = {
      status: Number(status),
      keyword: searchTerm || "",
      type: "ORDER",
      orderNo: searchTerm2 || "",
      exportType: "csv",
      branchId: branchIds || [],
      userId: "",
      orderType: updatedOrderType,
      platform: updatedPlatform,
      appVersion: platform?.length > 0 ? appVersion : "",
      dateRange:
        dateRange.min && dateRange.max
          ? { min: dateRange.min, max: dateRange.max }
          : {},
      userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }
    const response = await pdfDownload(EXPORT_DATA, payload)
    if (response) {
      const csvData = response
      DownloadCSV(csvData, t("Order"))
    }
  }

  // Convert bookingStatus to options array for react-select
  const statusOptions = Object.entries(orderStatus).map(([value, label]) => ({
    value,
    label: t(label),
  }))

  const handleStatusChange = (id, status) => {
    setSelectedId(id)
    const payload = {
      id,
      status,
    }
    dispatch(updateOrderStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const perPageOptions = getPerPageOptions(totalCount)

  const handlePerPageChange = e => {
    dispatch(setItemsPerPage(Number(e.target.value)))
  }

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col xl={12}>
            <Card>
              <DataExportDropdown
                onDownloadCSV={exportToCSV}
                onDownloadExcel={handleExcelDownload}
                cardTitle={t("Orders List")}
                isAddBtn={false}
                isExportData={loading ? false : true}
                isCustomPage={true}
                perPageOptions={perPageOptions}
                itemsPerPage={itemsPerPage}
                handlePerPageChange={handlePerPageChange}
              />
              <CardBody>
                {/* {!loading && ( */}
                <Filters
                  filters={filters}
                  searchTerm={searchTerm}
                  handleSearchChange={handleSearchChange}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleReset}
                  selectedFilters={selectedFilters}
                  pageSizes={[0, 1, 2]}
                  itemsPerPage={50}
                  setItemsPerPage={null}
                  isRangeDate={true}
                  dateRange={dateRange}
                  setDateRange={setDateRange}
                  flatpickrRef={flatpickrRef}
                  searchBy={t("Name, Phone number")}
                  isSecondSearch={true}
                  searchTerm2={searchTerm2}
                  handleSearchChange2={handleSearchChange2}
                  searchBy2={t("Order No.")}
                  colSize={4}
                />
                {/* )} */}
                <div className="table-responsive">
                  {loading ? (
                    <TableShimmer row={7} col={5} />
                  ) : orders.length === 0 ? (
                    <div className="g_center" style={{ minHeight: 200 }}>
                      <p className="text-center my-2 mb-2">
                        {t("No orders found")}
                      </p>
                    </div>
                  ) : (
                    <Table className="table mb-0" hover>
                      <thead className="table-light full_th">
                        <tr>
                          <th style={{ whiteSpace: "nowrap" }}>
                            {t("S. No.")}
                          </th>
                          <th>{t("Order Id")}</th>
                          <th>{t("Order No")}</th>
                          <th>{t("Name")}</th>
                          <th>{t("Phone Number")}</th>
                          {/* <th>Email</th> */}
                          <th>{t("Platform")}</th>
                          <th>{t("Amount")}</th>
                          <th>{t("Order Type")}</th>
                          <th>{t("Branch Name")}</th>
                          <th>{t("Order Date")}</th>
                          <th>{t("Promise Time")}</th>
                          <th>{t("Payment Method")}</th>
                          <th>{t("Receipt Id")}</th>
                          <th>{t("Status")}</th>
                          {usersPermissions.edit && (
                            <th style={{ minWidth: 200 }}>
                              {t("Update Status")}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((item, index) => (
                          <tr
                            key={item._id}
                            onClick={() => {
                              handleRowClick(item._id)
                            }}
                            className="cursor-pointer"
                          >
                            <th>
                              {" "}
                              {(currentPage - 1) * itemsPerPage + index + 1}
                            </th>

                            {/* <th scope="row">
                              <Link
                                className="text-decoration-underline gray_text"
                                to={`/orders/order-details?id=${item._id}`}
                              >
                                {item._id}
                              </Link>
                            </th> */}
                            <td>{item._id}</td>
                            <td>{item.invoiceNo}</td>

                            <td>{item?.name || "-"}</td>
                            <td>{item?.phone || "-"}</td>

                            <td>
                              {item.platform?.toLowerCase() === "android"
                                ? t("Android")
                                : item.platform?.toLowerCase() === "ios"
                                ? t("iOS")
                                : item.platform?.toLowerCase() === "web"
                                ? t("Web")
                                : "-"}
                              {item.platform?.toLowerCase() !== "web" &&
                                item?.appVersion &&
                                ` (${item.appVersion})`}
                            </td>

                            {/* <td>{item.email || "-"}</td> */}
                            <td>
                              {item.paidAmount} {item?.splitPayments?.currency}
                            </td>
                            <td className="text-capitalize">
                              {t(item.orderType)}
                            </td>
                            <td className="text-capitalize">
                              {item.branchName}
                            </td>
                            <td>{formatPromiseDateTime(item.createdAt)}</td>
                            <td>
                              {formatPromiseDateTime(item.promiseDateTime)}
                            </td>

                            <td>
                              <i
                                className={`${
                                  item.paymentMethod === "CARD"
                                    ? "fab fa-cc-mastercard me-1"
                                    : "" || item.paymentMethod === "APPLE"
                                    ? "fab bx bxl-apple me-1"
                                    : ""
                                }`}
                              ></i>
                              {t(item.paymentMethod)}
                            </td>
                            <td>{item?.receiptId || "-"}</td>

                            <td
                            // style={{
                            //   whiteSpace: "nowrap",
                            //   verticalAlign: "middle",
                            // }}
                            >
                              <span
                                style={{
                                  color:
                                    statusListStatus[item.orderStatus].color,
                                }}
                              >
                                {statusListStatus[item.orderStatus].text}
                              </span>
                            </td>
                            {/* <td>
                              {usersPermissions.list && (
                                <Button
                                  type="button"
                                  color="primary"
                                  className="btn-sm btn-rounded"
                                  onClick={() => {
                                    navigate(
                                      `/orders/order-details?id=${item._id}`
                                    )
                                  }}
                                >
                                  View Details
                                </Button>
                              )}
                            </td> */}

                            {usersPermissions.edit && (
                              <th onClick={e => e.stopPropagation()}>
                                <Select
                                  value={statusOptions.find(
                                    option =>
                                      option.value ===
                                      item.orderStatus.toString()
                                  )}
                                  isDisabled={
                                    selectedId === item._id && isLoading
                                  }
                                  onChange={(selectedOption, event) => {
                                    if (event.nativeEvent) {
                                      event.nativeEvent.stopPropagation()
                                    }
                                    handleStatusChange(
                                      item._id,
                                      selectedOption.value
                                    )
                                  }}
                                  options={statusOptions
                                    .filter(option => {
                                      const currentStatus = parseInt(
                                        item.orderStatus
                                      )
                                      const optionValue = parseInt(option.value)

                                      switch (currentStatus) {
                                        case 1:
                                          return (
                                            optionValue === 2 ||
                                            optionValue === 5
                                          )
                                        case 2:
                                          return (
                                            optionValue === 3 ||
                                            optionValue === 5
                                          )
                                        case 3:
                                          return (
                                            optionValue === 4 ||
                                            optionValue === 5
                                          )
                                        case 4:
                                          return false
                                        case 5:
                                          return false
                                        default:
                                          return false
                                      }
                                    })
                                    .map(option => ({
                                      ...option,
                                      isDisabled:
                                        parseInt(option.value) ===
                                        parseInt(item.orderStatus),
                                    }))}
                                  placeholder={t("Update Status")}
                                />
                              </th>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
                {!loading && orders.length > 0 && (
                  <div className="mt-3">
                    <Paginations
                      data={orders}
                      itemsPerPage={itemsPerPage}
                      onPageChange={onPageChange}
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalCount={totalCount}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
      {/* <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle="Are you sure you want to delete this user?"
      /> */}
    </div>
  )
}

export default Orders
